import companiesJson from '../../content/tickers/companies.json';
import cryptoJson from '../../content/tickers/crypto.json';
import moodsJson from '../../content/tickers/moods.json';
import predictionsJson from '../../content/tickers/predictions.json';
import type { PriceConfig } from '@/engine/prices';

export type Category = 'company' | 'crypto' | 'mood' | 'prediction' | 'geo';

export interface BaseTicker extends PriceConfig {
  id: string;
  name?: string;
  question?: string;       // for prediction markets
  lore?: string;
  category: Category;
}

export interface CompanyTicker extends BaseTicker {
  category: 'company';
  name: string;
  sector: string;
  ceo: string;
  marketCap: number;
  beta: number;
}

const companies: CompanyTicker[] = (companiesJson as Array<Omit<CompanyTicker, 'category' | 'ticker'>>).map((c) => ({
  ...c,
  ticker: c.id,
  category: 'company',
}));

const crypto: BaseTicker[] = (cryptoJson as Array<{ id: string; name: string; lore: string; baseline: number; drift: number; volatility: number }>).map((c) => ({
  ...c,
  ticker: c.id,
  category: 'crypto',
}));

const moods: BaseTicker[] = (moodsJson as Array<{ id: string; name: string; lore: string; baseline: number; drift: number; volatility: number }>).map((m) => ({
  ...m,
  ticker: m.id,
  category: 'mood',
}));

const predictions: BaseTicker[] = (predictionsJson as Array<{ id: string; question: string; category: string; baseline: number; drift: number; volatility: number }>).map((p) => ({
  ...p,
  ticker: p.id,
  category: 'prediction',
}));

const ALL_TICKERS: BaseTicker[] = [...companies, ...crypto, ...moods, ...predictions];
const byId = new Map<string, BaseTicker>();
for (const t of ALL_TICKERS) byId.set(t.id, t);

export function listCompanies(): CompanyTicker[] { return companies; }
export function listCrypto(): BaseTicker[] { return crypto; }
export function listMoods(): BaseTicker[] { return moods; }
export function listPredictions(): BaseTicker[] { return predictions; }

export function listByCategory(c: Category): BaseTicker[] {
  switch (c) {
    case 'company': return companies;
    case 'crypto': return crypto;
    case 'mood': return moods;
    case 'prediction': return predictions;
    case 'geo': return [];
  }
}

export function listAll(): BaseTicker[] { return ALL_TICKERS; }

export function getTicker(id: string): BaseTicker | undefined {
  return byId.get(id);
}

export function tickerIds(): string[] {
  return ALL_TICKERS.map((c) => c.id);
}

export function tickerLabel(t: BaseTicker): string {
  return t.name ?? t.question ?? t.id;
}
